import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadSimple, FileArrowDown, ArrowLeft, CheckCircle } from "@phosphor-icons/react";

export default function ImportInvoicesDialog({ open, onOpenChange, contact }) {
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [errorCount, setErrorCount] = useState(0);
  const [total, setTotal] = useState(0);

  const reset = () => {
    setStep(1);
    setFile(null);
    setPreview([]);
    setErrors([]);
    setErrorCount(0);
    setTotal(0);
  };

  const close = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const downloadTemplate = async (ext) => {
    try {
      const res = await api.get(`/invoices/template.${ext}`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = `fatura-sablon.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Şablon indirildi (fatura-sablon.${ext})`);
    } catch (e) {
      toast.error("Şablon indirilemedi");
    }
  };

  const onFile = (f) => {
    setFile(f);
    if (f) setStep(2);
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("commit", "false");
      const { data } = await api.post(`/contacts/${contact.id}/invoices/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data.preview || []);
      setErrors(data.errors || []);
      setErrorCount(data.error_count || 0);
      setTotal(data.total_rows || 0);
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Önizleme başarısız");
    } finally {
      setBusy(false);
    }
  };

  const runCommit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("commit", "true");
      const { data } = await api.post(`/contacts/${contact.id}/invoices/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.inserted} fatura aktarıldı (${data.skipped} atlandı)`);
      qc.invalidateQueries({ queryKey: ["invoices", contact.id] });
      close(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktarma başarısız");
    } finally {
      setBusy(false);
    }
  };

  const dirBadge = (d) => (
    <span className={`inline-block rounded-sm px-2 py-0.5 text-[10px] font-semibold ${
      d === "outgoing" ? "bg-emerald-50 text-emerald-700" : "bg-indigo-50 text-indigo-700"
    }`}>{d === "outgoing" ? "Giden" : "Gelen"}</span>
  );

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Toplu Fatura Aktar — {contact?.name}{" "}
            <span className="text-xs text-muted-foreground font-normal">
              ({contact?.kind === "supplier" ? "Tedarikçi" : "Müşteri"})
            </span>
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4" data-testid="inv-import-step-1">
            <div className="rounded-md border border-border bg-secondary/30 p-4">
              <div className="overline mb-2">Adım 1 — Şablon İndir</div>
              <div className="text-sm text-muted-foreground mb-3">
                Kolonlar: date, number, description, amount, currency (TRY/USD/EUR),
                direction (outgoing/giden veya incoming/gelen), status (pending/bekliyor
                veya paid/ödendi), due_date.
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => downloadTemplate("csv")} data-testid="tpl-csv">
                  <FileArrowDown size={14} className="mr-1" /> CSV Şablon
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadTemplate("xlsx")} data-testid="tpl-xlsx">
                  <FileArrowDown size={14} className="mr-1" /> Excel Şablon
                </Button>
              </div>
            </div>
            <div className="rounded-md border border-dashed border-border p-8 text-center">
              <UploadSimple size={32} className="mx-auto text-muted-foreground" />
              <div className="mt-3 text-sm text-muted-foreground">
                Adım 2 — Doldurulmuş dosyanızı seçin (.csv veya .xlsx)
              </div>
              <Input
                type="file"
                accept=".csv,.xlsx"
                onChange={(e) => onFile(e.target.files?.[0] || null)}
                className="mt-4 mx-auto max-w-sm"
                data-testid="inv-import-file"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="inv-import-step-2">
            <div className="rounded-md border border-border bg-secondary/30 p-3 text-sm">
              <span className="mono font-semibold">{file?.name}</span>
              <span className="ml-2 text-xs text-muted-foreground">
                ({((file?.size || 0) / 1024).toFixed(1)} KB)
              </span>
            </div>
            <div className="text-sm text-muted-foreground">
              Dosyayı önizlemeye hazır. Sistem satırları ayrıştırıp hangilerinin geçerli
              olduğunu gösterecek.
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)} data-testid="back-to-template">
                <ArrowLeft size={14} className="mr-1" /> Geri
              </Button>
              <Button onClick={runPreview} disabled={busy} data-testid="inv-run-preview">
                {busy ? "İşleniyor..." : "Önizle →"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="inv-import-step-3">
            <div className="flex flex-wrap gap-3 text-sm">
              <div className="rounded-md border border-border bg-white px-3 py-2">
                <div className="overline">Geçerli Satır</div>
                <div className="mono text-emerald-700 font-semibold">{total}</div>
              </div>
              <div className="rounded-md border border-border bg-white px-3 py-2">
                <div className="overline">Hatalı Satır</div>
                <div className="mono text-red-600 font-semibold">{errorCount}</div>
              </div>
            </div>
            <div className="max-h-80 overflow-auto rounded-md border border-border">
              <table className="w-full text-xs" data-testid="inv-preview-table">
                <thead className="border-b border-border bg-secondary/50">
                  <tr className="text-left uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2">Satır</th>
                    <th className="px-3 py-2">Tarih</th>
                    <th className="px-3 py-2">No</th>
                    <th className="px-3 py-2">Yön</th>
                    <th className="px-3 py-2">Açıklama</th>
                    <th className="px-3 py-2 text-right">Tutar</th>
                    <th className="px-3 py-2">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((p) => (
                    <tr key={p.line} className="border-b border-border/60">
                      <td className="px-3 py-1.5 mono text-muted-foreground">{p.line}</td>
                      <td className="px-3 py-1.5 mono">{fmtDate(p.date)}</td>
                      <td className="px-3 py-1.5 mono">{p.number || "-"}</td>
                      <td className="px-3 py-1.5">{dirBadge(p.direction)}</td>
                      <td className="px-3 py-1.5 text-muted-foreground max-w-xs truncate">{p.description || "-"}</td>
                      <td className="px-3 py-1.5 mono text-right">{fmtMoney(p.amount, p.currency)}</td>
                      <td className="px-3 py-1.5">
                        <span className={`inline-block rounded-sm px-1.5 py-0.5 text-[10px] ${
                          p.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                        }`}>{p.status === "paid" ? "Ödendi" : "Beklemede"}</span>
                      </td>
                    </tr>
                  ))}
                  {preview.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Önizlenecek satır yok</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {errors.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">
                  {errors.length} hata detayını göster
                </summary>
                <ul className="mt-2 space-y-1">
                  {errors.map((er, i) => (
                    <li key={i} className="text-red-600">
                      Satır {er.line}: {er.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)} data-testid="back-to-upload">
                <ArrowLeft size={14} className="mr-1" /> Yeni Dosya
              </Button>
              <Button
                onClick={runCommit}
                disabled={busy || total === 0}
                data-testid="inv-run-commit"
              >
                {busy ? "Aktarılıyor..." : (
                  <>
                    <CheckCircle size={14} className="mr-1" /> {total} Faturayı Aktar
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
